import React, { useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { reportApi } from '../../../../../api/reportApi';
import ReportStatusDisplay from '../../../report/components/ReportStatusDisplay';

const DESTINATION_LABELS = { faicon: 'FAICON', wordpress: 'WordPress' };

/** One PDF for a single language, otherwise one ZIP so the browser does not drop parallel downloads. */
async function downloadReportPdfs(sessionId, primaryLanguage) {
  let langs = [primaryLanguage];
  try {
    const res = await reportApi.getReportLanguages(sessionId);
    const completed = (res?.languages || [])
      .filter((l) => l?.code && String(l.status).toUpperCase() === 'COMPLETED')
      .map((l) => l.code);
    if (completed.length) langs = completed;
  } catch {
    // Fall back to the primary language only.
  }

  if (langs.length === 1) {
    await reportApi.downloadSessionReportPdf(sessionId, langs[0]);
    return { type: 'success', title: 'PDF Downloaded', message: 'Report has been downloaded as PDF' };
  }
  const { included, failed } = await reportApi.downloadSessionReportPdfBundle(sessionId, langs);
  if (!included.length) throw new Error(`Could not generate PDFs for: ${failed.join(', ')}`);
  return failed.length
    ? { type: 'warning', title: 'Partial download', message: `Downloaded ${included.length} PDFs (${included.join(', ')}). Failed for: ${failed.join(', ')}` }
    : { type: 'success', title: 'PDFs Downloaded', message: `Downloaded ZIP with ${included.length} PDFs (${included.join(', ')})` };
}

/** Official report: generation progress, (re)generate, PDF and publishing. */
export default function ReportCard({ session, report, publish, canGenerate, onGenerate, onAdvanced, primaryLanguage, notify }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const { status } = report;
  const completed = status?.status === 'COMPLETED';
  const translating = completed && (status.progress ?? 100) < 100;

  const downloadPdf = async () => {
    setIsDownloading(true);
    try {
      notify(await downloadReportPdfs(session.id, primaryLanguage));
    } catch (e) {
      notify({ type: 'error', title: 'Failed to download PDF', message: e.message || 'Could not generate PDF' });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <div className="session-detail-card">
        <h3>Report</h3>
        {status && (report.busy || status.status === 'FAILED') && (
          <ReportStatusDisplay
            status={translating ? 'PROCESSING' : status.status}
            startedAt={status.processing_started_at}
            completedAt={status.processing_completed_at}
            queuedAt={status.queued_at}
            duration={status.processing_duration}
            errorMessage={status.error_message}
            queuePosition={status.queue_position}
            estimatedWaitTime={status.estimated_wait_time}
            currentStep={translating ? (status.current_step || 'Translating report...') : status.current_step}
            progress={status.progress}
            compact
          />
        )}
        <div className="session-side-actions">
          <button type="button" className="session-btn session-btn-secondary" onClick={onGenerate} disabled={!canGenerate}>
            <DocumentTextIcon />
            {report.isStarting ? 'Generating...' : report.exists ? 'Regenerate report' : 'Generate report'}
          </button>
          <button type="button" className="session-btn session-btn-ghost" onClick={onAdvanced} disabled={!canGenerate} title="Customize prompts">
            <Cog6ToothIcon /> Advanced config
          </button>
          {completed && (
            <button type="button" className="session-btn session-btn-ghost" onClick={downloadPdf} disabled={isDownloading}>
              {isDownloading ? <ArrowPathIcon className="animate-spin" /> : <ArrowDownTrayIcon />}
              {isDownloading ? 'Preparing PDF...' : 'Download report PDF'}
            </button>
          )}
        </div>
      </div>

      {completed && publish.destinations.length > 0 && (
        <div className="session-detail-card">
          <h3>Publishing</h3>
          {session.url && (
            <div className="session-published-mini">
              <span className="session-detail-badge session-detail-badge-published">Published</span>
              <a href={session.url} target="_blank" rel="noopener noreferrer" title={session.url}>{session.url}</a>
            </div>
          )}
          <div className="session-side-actions">
            {publish.destinations.map((dest) => (
              <button
                key={dest}
                type="button"
                className="session-btn session-btn-ghost"
                onClick={() => publish.open(dest)}
                disabled={publish.isPublishing}
              >
                <ArrowTopRightOnSquareIcon />
                {publish.isPublishing ? 'Publishing...' : `${session.url ? 'Update' : 'Publish to'} ${DESTINATION_LABELS[dest]}`}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
